import * as React from 'react'
import { connect } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Moment from 'react-moment'
import * as CurrencyFormat from 'react-currency-format'
import { RouteComponentProps, withRouter } from 'react-router'
import { Modal, Button, ModalBody, ModalHeader, ModalFooter } from 'reactstrap'

import { RootState } from '../../store/rootReducer'
import { Accident } from '../../lib/interfaces/Accident.interface'
import { Employee } from '../../lib/interfaces/Employee.interface'
import { User, hasPermission } from '../../lib/interfaces/User.interface'
import { ClaimInvoice } from '../../lib/interfaces/ClaimInvoice.interface'

import Breadcrumbs from '../Breadcrumbs.component'
import FileList from '../../common/FileList.component'
import ClaimsList from 'src/common/ClaimsList.component'
import FileDropzone from '../../common/FileDropzone.component'
import InvoicePaymentModal from './components/InvoicePayment.modal'

import * as invoicesActions from '../../store/invoices/actions'
import * as accidentsActions from '../../store/accidents/actions'
import * as employeesActions from '../../store/employees/actions'
import InvoiceTrackingModal from './components/InvoiceTracking.modal'

const moment = require('moment')

const get = require('lodash/get')
const compact = require('lodash/compact')

interface RouteInfo {
  invoiceId: string
}

export interface InvoiceDetailsProps extends RouteComponentProps<RouteInfo> {
  user: User
  fetchFilesError?: any
  invoice?: ClaimInvoice
  isFetchingFiles: boolean
  isUploadingFile: boolean
  isDeletingInvoice: boolean
  setCurrentEmployee: (employee: Employee) => void
  setCurrentAccident: (accident?: Accident) => void
  fetchInvoiceFiles: (invoiceId: number|string) => void
  uploadInvoiceFile: (invoiceId: number|string, file: any) => void
  deleteInvoice: (invoiceId: string|number, next?: (err?: any) => void) => void
  fetchInvoice: (invoiceId: number|string, next?: (error?: any, invoice?: ClaimInvoice) => void) => void
}

export interface InvoiceDetailsState {
  invoicePaymentModalOpen: boolean
  invoiceTrackingModalOpen: boolean
  invoiceDeletionModalOpen: boolean
}

class InvoiceDetails extends React.PureComponent<InvoiceDetailsProps, InvoiceDetailsState> {
  state: InvoiceDetailsState = {
    invoicePaymentModalOpen: false,
    invoiceTrackingModalOpen: false,
    invoiceDeletionModalOpen: false
  }

  get invoiceId() {
    return this.props.match.params.invoiceId
  }

  componentDidMount() {
    const { fetchInvoice } = this.props
    fetchInvoice(this.invoiceId, (error?: any, invoice?: ClaimInvoice) => {
      if (!error) {
        this.fetchFiles()
      }
    })
  }

  fetchFiles = () => {
    this.props.fetchInvoiceFiles(this.invoiceId)
  }

  getExpirationStatusText(claim) {
    const now = moment()
    const dueDate = moment(claim.lossDate).add(60, 'day')

    const duration = moment.duration(dueDate.diff(now))
    const daysDiff = duration.asDays()

    let expirationText
    if (daysDiff <= -1) {
      const expiredDays = Math.floor(Math.abs(daysDiff))
      expirationText = `Expired ${expiredDays} days ago`
    }
    else if (daysDiff >= -1 && daysDiff <= 1) {
      expirationText = 'Expires today'
    }
    else {
      expirationText = `Expires in ${Math.floor(daysDiff)} days`
    }

    return expirationText
  }

  handleFileUpload = file => {
    const { uploadInvoiceFile } = this.props
    uploadInvoiceFile(this.invoiceId, file)
  }

  handleDeleteInvoice = () => {
    const { deleteInvoice } = this.props
    deleteInvoice(this.invoiceId, (err?: any) => {
      if (!err) {
        this.props.history.push('/invoices')
      }
    })
  }

  renderInvoiceDeletionConfirmationModal() {
    const { isDeletingInvoice } = this.props
    const { invoiceDeletionModalOpen } = this.state

    return (
      <Modal isOpen={invoiceDeletionModalOpen}>
        <ModalHeader toggle={() => this.setState({ invoiceDeletionModalOpen: false })}>Delete claim</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this invoice?
        </ModalBody>
        <ModalFooter>
          <Button color="light" className="ggbutton" onClick={() => this.setState({ invoiceDeletionModalOpen: false })}>Cancel</Button>
          <Button color="primary" disabled={isDeletingInvoice} className="ggbutton" onClick={this.handleDeleteInvoice}>
            {isDeletingInvoice &&
              <FontAwesomeIcon
                spin
                className="mr-3"
                icon={['fas', 'spinner']}
              />
            }
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  renderDate(date) {
    return !date ? '-' : <Moment date={date} format="MM.DD.YYYY" />
  }

  renderEmployee(employee?: Employee) {
    const { setCurrentEmployee } = this.props

    if (!employee) {
      return '-'
    }

    const employeeName = compact([ employee.firstName, employee.lastName ])
    return (
      <a className="cursor-pointer" onClick={() => setCurrentEmployee(employee)}>
        <u>{employeeName.join(' ')}</u>
      </a>
    )
  }

  render() {
    const { invoicePaymentModalOpen, invoiceTrackingModalOpen } = this.state
    const { user, invoice, fetchFilesError, isFetchingFiles, isUploadingFile } = this.props

    return (
      <div>
        <InvoicePaymentModal invoiceId={this.invoiceId} open={invoicePaymentModalOpen} onClose={() => this.setState({ invoicePaymentModalOpen: false })} />
        <InvoiceTrackingModal invoiceId={this.invoiceId} open={invoiceTrackingModalOpen} onClose={() => this.setState({ invoiceTrackingModalOpen: false })} />

        <Breadcrumbs breadcrumbs={[
          { name: 'Invoices', link: '/invoices' },
          { name: `Invoice ${this.invoiceId}`, active: true }
        ]} />

        {invoice &&
          <div className="accident-page mt-4">
            <section className="container-fluid pl-0 pr-0 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">Invoice Status & Details</h5>
                {hasPermission(user, 'ROLE_ADMIN') &&
                  <React.Fragment>
                    {this.renderInvoiceDeletionConfirmationModal()}

                    {!invoice.isPaid &&
                      <Button
                        size="sm"
                        color="primary"
                        className="ggbutton ml-3"
                        onClick={() => this.setState({ invoicePaymentModalOpen: true })}
                      >
                        Pay
                      </Button>
                    }

                    <Button
                      size="sm"
                      color="dark"
                      className="ggbutton ml-3"
                      onClick={() => this.setState({ invoiceDeletionModalOpen: true })}
                    >
                      Delete
                    </Button>

                    <Button
                      size="sm"
                      color="dark"
                      className="ggbutton ml-3"
                      onClick={() => this.setState({ invoiceTrackingModalOpen: true })}
                    >
                      Add Tracking
                    </Button>
                  </React.Fragment>
                }
              </div>

              <div className="row no-gutters mt-4">
                <div className="col-12 col-md-8 col-lg-4 pr-md-3">
                  <div className="ggcard p-3">
                    <table>
                      <tbody>
                        <tr>
                          <td>Date created:</td>
                          <td>{this.renderDate(invoice.dateCreated)}</td>
                        </tr>
                        <tr>
                          <td>Payment date:</td>
                          <td>{this.renderDate(invoice.datePaid)}</td>
                        </tr>
                        <tr>
                          <td>Paid by:</td>
                          <td>{this.renderEmployee(invoice.paidBy)}</td>
                        </tr>
                        <tr>
                          <td>Check number:</td>
                          <td>{invoice.checkNumber}</td>
                        </tr>
                        <tr>
                          <td>Tracking NUmber:</td>
                          <td>{invoice.mailTracking}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-6 col-sm-4 col-lg-2 pr-0 pr-sm-3 mt-3 mt-lg-0">
                  <div className="ggcard h-100 p-3">
                    <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                      <span className="costlabel">
                        {get(invoice, 'claims.length', 0)}
                      </span>
                      <span>Total Claims</span>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-5 col-lg-3 pr-0 pr-sm-3 mt-3 mt-lg-0">
                  <div className="ggcard h-100 p-3">
                    <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                      <span className="costlabel">
                        <CurrencyFormat
                          prefix="$"
                          decimalScale={2}
                          displayType="text"
                          value={invoice.cost}
                          thousandSeparator={true}
                          fixedDecimalScale={true}
                        />
                      </span>
                      <span>Total Cost</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="pl-0 mt-5 mb-4 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">Claims Attached To This Invoice</h5>
              </div>

              <ClaimsList
                viewMode="table"
                className="mt-2"
                claims={invoice.claims}
                emptyMessage="There are no claims attached to this invoice."
                onRowClick={claim =>
                  this.props.setCurrentAccident(claim.accident)
                }
                columns={[
                  'erac',
                  'location',
                  'vin',
                  'vehicle',
                  'driver',
                  'status',
                  'claim-type',
                  'loss-date',
                  'cost'
                ]}
              />
            </section>

            <section className="pl-0 mt-5 mb-4 ml-0">
              <div className="d-flex align-items-center mb-1">
                <h5 className="title mb-0">Check Scan</h5>
                {isFetchingFiles && <FontAwesomeIcon spin className="ml-2" icon={['fas', 'spinner']} style={{ fontSize: '20px' }} />}
              </div>

              <div className="mt-4">
                <FileList
                  entity="invoice"
                  files={invoice.files}
                  entityId={invoice.id}
                  error={fetchFilesError}
                  onDelete={this.fetchFiles}
                  className="row no-gutters"
                  onTryAgain={this.fetchFiles}
                  fileWrapperClassName="col-4 pr-3 pb-3"
                  canDelete={hasPermission(user, 'ROLE_ADMIN')}
                />
                
                <div className="w-100 mt-4">
                  <FileDropzone isLoading={isUploadingFile} onDrop={this.handleFileUpload} title="Browse for a replacement or drop one here" />
                </div>
              </div>
            </section>
          </div>
        }
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
  invoice: state.invoices.invoiceDetails,
  isDeletingInvoice: state.invoices.isDeleting,
  fetchFilesError: state.invoices.fetchFilesError,
  isFetchingFiles: state.invoices.isFetchingFiles,
  isUploadingFile: state.invoices.isUploadingFile
})

const mapDispatchToProps = (dispatch) => ({
  setCurrentEmployee: (employee: Employee) => dispatch(employeesActions.setCurrentEmployee(employee)),
  setCurrentAccident: (accident?: Accident) => dispatch(accidentsActions.setCurrentAccident(accident)),
  fetchInvoiceFiles: (invoiceId: number|string) => dispatch(invoicesActions.fetchInvoiceFiles(invoiceId)),
  uploadInvoiceFile: (invoiceId: number|string, file: any) => dispatch(invoicesActions.uploadInvoiceFile(invoiceId, file)),
  deleteInvoice: (invoiceId: string|number, next?: (err?: any) => void) => dispatch(invoicesActions.deleteInvoice(invoiceId, next)),
  fetchInvoice: (invoiceId: number|string, next?: (error?: any, invoice?: ClaimInvoice) => void) => dispatch(invoicesActions.fetchInvoice(invoiceId, next))
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InvoiceDetails))



// WEBPACK FOOTER //
// ./src/routes/invoices/InvoiceDetails.page.tsx